import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ENROLLMENT_STATUS_LABEL,
  FLAG_ICON,
  FLAG_LABEL,
  FLAG_TYPES,
  STUDENT_STATUS_LABEL,
  formatWeekdays,
  stageLabel,
  type FlagType,
} from '@doit/shared';
import { ArrowLeft, Mail, Pencil, Phone, TriangleAlert, Undo2, UserMinus, X } from 'lucide-react';
import clsx from 'clsx';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Alert, Button, Card, EmptyState, FullPageLoader, PageHeader } from '../../components/ui';
import { Textarea } from '../../components/form';
import { Badge, DirectionTag, GroupStatusBadge, ProjectBadge } from '../../components/badges';
import { Select } from '../../components/form';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import { useCurrentUser } from '../../api/auth';
import {
  useDropEnrollment,
  useRemoveFlag,
  useSetFlag,
  useProjects,
  useSettings,
  useStudentCard,
  useUpdateEnrollment,
} from '../../api/queries';
import type { CardEnrollment } from '../../api/types';
import { formatDate, formatPercent } from '../../lib/format';
import { zoneColor } from '../../lib/zone';
import { AttendanceTimeline } from './AttendanceTimeline';
import { StageResults } from './StageResults';
import { StudentNotes } from './StudentNotes';
import { StudentModal } from './StudentModal';

export function StudentCardPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const toast = useToast();
  const isStaff = user.role !== 'LECTURER';

  const { data, isLoading, error } = useStudentCard(id);
  const { data: settings } = useSettings();
  const { data: projects } = useProjects();
  const updateEnrollment = useUpdateEnrollment();

  const dropEnrollment = useDropEnrollment();

  const [activeIndex, setActiveIndex] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [dropReason, setDropReason] = useState('');
  // არჩეული ეტაპი — null ნიშნავს „მიმდინარეს"; ჩარიცხვის გადართვაზე უბრუნდება
  const [stageTab, setStageTab] = useState<number | null>(null);

  if (isLoading) return <FullPageLoader />;

  if (error || !data) {
    return (
      <div className="rounded-[10px] border border-line bg-surface shadow-xs">
        <EmptyState
          title="სტუდენტი მიუწვდომელია"
          description={error instanceof ApiError ? error.message : 'ჩანაწერი ვერ მოიძებნა'}
          action={
            <Button variant="secondary" onClick={() => navigate(-1)}>
              უკან
            </Button>
          }
        />
      </div>
    );
  }

  const { student, enrollments } = data;
  const active = enrollments[activeIndex];

  // ლექტორი ყოველთვის მიმდინარე ეტაპზეა — ისტორია მენეჯერის ხედია
  const shownStage = isStaff ? (stageTab ?? active?.currentStage ?? 1) : (active?.currentStage ?? 1);
  const isCurrentStage = shownStage === active?.currentStage;

  /** რისკი და ფლაგი ყოველთვის მიმდინარე ეტაპზე ითვლება */
  const stats = active?.currentStageStats;

  // ეტაპზე შეიძლება რამდენიმე ჩანაწერი იყოს (დავალება + პროექტი) — მეტრიკებს ისტორიული კვებავს
  const stageResultsForStage = active?.stageResults.filter((r) => r.stage === shownStage) ?? [];
  const stageResult =
    stageResultsForStage.find((r) => r.isHistorical) ?? stageResultsForStage[0] ?? null;
  const stageTimeline = (active?.stats?.timeline ?? []).filter((p) => p.stage === shownStage);

  // არჩეულ ეტაპზე ნაჩვენები მაჩვენებლები: მიმდინარეზე — ჟურნალიდან, წარსულზე — შედეგიდან
  const shownPct = isCurrentStage ? (stats?.pct ?? null) : (stageResult?.attendancePct ?? null);

  /** ჩარიცხვის პროექტი — ჯგუფისგან დამოუკიდებელია, ამიტომ ცალკე იცვლება */
  async function changeProject(projectId: string) {
    if (!active || projectId === active.project.id) return;
    try {
      await updateEnrollment.mutateAsync({ id: active.id, data: { projectId } });
      toast.success('პროექტი შეიცვალა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ შეიცვალა');
    }
  }

  /** რეალური შეწყვეტა — ჩარიცხვა იხურება და სტუდენტი აქტიური სიებიდან გადის */
  async function drop() {
    if (!active) return;
    try {
      await dropEnrollment.mutateAsync({ id: active.id, reason: dropReason.trim() || undefined });
      setDropOpen(false);
      setDropReason('');
      toast.success('სწავლა შეწყვეტილია');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ შეწყდა');
    }
  }

  /** შეცდომით დაჭერის გამოსწორება — ჩარიცხვა და ⚫ ნიშანი ერთად ბრუნდება */
  async function restore() {
    if (!active) return;
    try {
      await updateEnrollment.mutateAsync({ id: active.id, data: { status: 'ACTIVE' } });
      toast.success('ჩარიცხვა აქტიურში დაბრუნდა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ დაბრუნდა');
    }
  }

  return (
    <>
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-ink-mute transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        უკან
      </button>

      <PageHeader
        title={student.fullName}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Badge tone={student.status === 'ACTIVE' ? 'ok' : 'neutral'}>
              {STUDENT_STATUS_LABEL[student.status]}
            </Badge>
            {student.phone && (
              <a href={`tel:${student.phone}`} className="tabular flex items-center gap-1 hover:text-ink">
                <Phone className="size-3.5" />
                {student.phone}
              </a>
            )}
            {student.email && (
              <a href={`mailto:${student.email}`} className="flex items-center gap-1 hover:text-ink">
                <Mail className="size-3.5" />
                {student.email}
              </a>
            )}
            {student.academyEmail && <span>{student.academyEmail}</span>}
          </span>
        }
        action={
          isStaff ? (
            <Button
              variant="secondary"
              icon={<Pencil className="size-4" />}
              onClick={() => setEditOpen(true)}
            >
              რედაქტირება
            </Button>
          ) : undefined
        }
      />

      {enrollments.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface shadow-xs">
          <EmptyState
            title="სტუდენტი ჯგუფში არ არის"
            description="ჩარიცხე ჯგუფში, რომ დასწრების აღრიცხვა დაიწყოს."
          />
        </div>
      ) : (
        <>
          {/* --- ჩარიცხვების ტაბები --- */}
          {enrollments.length > 1 && (
            <nav className="mb-4 flex flex-wrap gap-1 border-b border-line">
              {enrollments.map((e, i) => (
                <button
                  key={e.id}
                  onClick={() => {
                    setActiveIndex(i);
                    setStageTab(null);
                  }}
                  className={clsx(
                    '-mb-px border-b-2 px-3.5 py-2 text-[13px] font-medium transition',
                    i === activeIndex
                      ? 'border-brand-600 text-brand-700'
                      : 'border-transparent text-ink-mute hover:text-ink',
                  )}
                >
                  <span className="tabular">{e.group.name}</span>
                  {e.status !== 'ACTIVE' && (
                    <span className="ml-1.5 text-[11px]">
                      ({ENROLLMENT_STATUS_LABEL[e.status]})
                    </span>
                  )}
                </button>
              ))}
            </nav>
          )}

          {active && (
            <>
              {/* --- ეტაპები: წარსული ისტორიაა, მიმდინარე ივსება; ლექტორს მხოლოდ მიმდინარე --- */}
              {isStaff && active.stageCount > 1 && (
                <div className="mb-5 flex flex-wrap items-center gap-2">
                  {Array.from({ length: active.stageCount }, (_, i) => i + 1).map((stage) => {
                    const isCurrent = stage === active.currentStage;
                    const isFuture = stage > active.currentStage;
                    const selected = stage === shownStage;
                    return (
                      <button
                        key={stage}
                        onClick={() => setStageTab(stage)}
                        disabled={isFuture}
                        className={clsx(
                          'rounded-full px-3.5 py-1.5 text-[13px] font-medium transition',
                          selected
                            ? 'bg-brand-600 text-white'
                            : isFuture
                              ? 'cursor-not-allowed text-ink-mute/50'
                              : 'bg-canvas text-ink-soft hover:bg-line/60',
                        )}
                      >
                        {stageLabel(stage)}
                        <span className="ml-1.5 text-[11px] opacity-70">
                          {isCurrent ? 'მიმდინარე' : isFuture ? 'დაგეგმილი' : 'ისტორია'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* --- მაჩვენებლები --- */}
              {isCurrentStage ? (
                <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <Metric
                    label="დასწრება"
                    value={formatPercent(stats?.pct, 1, 'ჯერ არ არის')}
                    color={zoneColor(stats?.pct, settings)}
                  />
                  <Metric
                    label="ზედიზედ გაცდენა"
                    value={String(stats?.streak ?? 0)}
                    danger={(stats?.streak ?? 0) >= (settings?.streak_risk_min ?? 3)}
                  />
                  <Metric
                    label="გაცდენა / გაზომვადი"
                    value={`${stats?.absent ?? 0} / ${stats?.countable ?? 0}`}
                  />
                  <Metric
                    label="ჩატარებული გაკვეთილი"
                    value={`${stats?.marked ?? 0} / ${active.group.totalLessons || '?'}`}
                  />
                </div>
              ) : (
                <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <Metric
                    label={`დასწრება · ${stageLabel(shownStage)}`}
                    value={formatPercent(shownPct, 1, 'არ არის')}
                    color={zoneColor(shownPct, settings)}
                  />
                  <Metric
                    label="შედეგი"
                    value={stageResult?.label ?? (stageResult?.score?.toString() ?? '—')}
                  />
                  <Metric label="სერტიფიკატი" value={stageResult?.certStatus ?? '—'} />
                  <Metric
                    label="დასწრების ჩანაწერი"
                    value={stageTimeline.length ? `${stageTimeline.length} გაკვეთილი` : 'ისტორია'}
                  />
                </div>
              )}

              {!isCurrentStage && (
                <p className="mb-5 rounded-lg bg-canvas px-3.5 py-2.5 text-[12px] text-ink-mute">
                  {stageLabel(shownStage)} დასრულებულია — მონაცემები ისტორიულია და აღარ იცვლება.
                  მიმდინარე შედეგებისთვის აირჩიე {stageLabel(active.currentStage)}.
                </p>
              )}

              <div className="grid gap-5 lg:grid-cols-3">
                {/* --- ჩარიცხვის ინფო: ლექტორს არ სჭირდება, ის ისედაც თავის ჯგუფშია --- */}
                {isStaff && (
                <Card
                  title="ჩარიცხვა"
                  className="lg:col-span-2"
                  action={
                    active.status === 'ACTIVE' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<UserMinus className="size-4" />}
                        onClick={() => setDropOpen(true)}
                      >
                        სწავლის შეწყვეტა
                      </Button>
                    ) : active.status === 'DROPPED' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<Undo2 className="size-4" />}
                        onClick={() => void restore()}
                        loading={updateEnrollment.isPending}
                      >
                        დაბრუნება
                      </Button>
                    ) : undefined
                  }
                >
                  {active.status === 'DROPPED' && (
                    <div className="mb-4">
                      <Alert kind="error">
                        <p className="font-medium">
                          სწავლა შეწყვეტილია
                          {active.leftAt ? ` · ${formatDate(active.leftAt)}` : ''}
                        </p>
                        <p className="mt-0.5 opacity-80">
                          ჟურნალის აქტიურ სიაში აღარ ჩანს, დასწრება აღარ ეღრიცხება და
                          საშუალოებში არ ითვლება.
                        </p>
                      </Alert>
                    </div>
                  )}
                  <dl className="grid gap-x-8 sm:grid-cols-2">
                    <Row
                      label="ჯგუფი"
                      value={
                        <Link
                          to={`/groups/${active.group.id}`}
                          className="tabular font-medium text-brand-700 hover:underline"
                        >
                          {active.group.name}
                        </Link>
                      }
                    />
                    <Row label="კურსი" value={active.group.course.name} />
                    <Row label="ნაკადი" value={active.group.flow.name} />
                    <Row
                      label="მიმართულება"
                      value={
                        <DirectionTag
                          name={active.group.course.direction.name}
                          color={active.group.course.direction.color}
                        />
                      }
                    />
                    <Row
                      label="განრიგი"
                      value={
                        active.group.weekdays.length
                          ? `${formatWeekdays(active.group.weekdays, true)} · ${active.group.startTime}`
                          : '—'
                      }
                    />
                    <Row
                      label="ლექტორი"
                      value={active.lecturers.map((l) => l.fullName).join(', ') || '—'}
                    />
                    <Row
                      label="ჩარიცხვის სტატუსი"
                      value={<Badge>{ENROLLMENT_STATUS_LABEL[active.status]}</Badge>}
                    />
                    <Row
                      label="პროექტი"
                      value={
                        isStaff ? (
                          <Select
                            className="max-w-52"
                            value={active.project.id}
                            onChange={(e) => void changeProject(e.target.value)}
                            options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
                          />
                        ) : (
                          <ProjectBadge project={active.project} />
                        )
                      }
                    />
                    <Row
                      label="ეტაპი"
                      value={`${stageLabel(active.currentStage)} / ${active.stageCount}`}
                    />
                    <Row label="ჩარიცხვის თარიღი" value={formatDate(active.enrolledAt)} />
                    <Row
                      label="ჯგუფის სტატუსი"
                      value={<GroupStatusBadge status={active.group.status} />}
                    />
                  </dl>
                </Card>
                )}

                {/* --- რისკის სტატუსი: მენეჯერის ინსტრუმენტი --- */}
                {isStaff && (
                <Card
                  title="რისკის სტატუსი"
                  action={
                    isStaff ? (
                      <Button size="sm" variant="secondary" onClick={() => setFlagOpen(true)}>
                        {active.flag ? 'შეცვლა' : 'დაყენება'}
                      </Button>
                    ) : undefined
                  }
                >
                  {active.flag ? (
                    <div className="space-y-2">
                      <p className="text-[15px] font-medium text-ink">
                        {FLAG_ICON[active.flag.flagType]} {FLAG_LABEL[active.flag.flagType]}
                      </p>
                      {active.flag.note && (
                        <p className="rounded-lg bg-canvas px-3 py-2 text-[13px] text-ink-soft">
                          {active.flag.note}
                        </p>
                      )}
                      <p className="text-[12px] text-ink-mute">
                        {active.flag.createdBy?.fullName ?? '—'} ·{' '}
                        {formatDate(active.flag.updatedAt)}
                      </p>
                      {active.flag.snapshotPct !== null && (
                        <p className="text-[12px] text-ink-mute">
                          დაყენებისას: {formatPercent(active.flag.snapshotPct)} → ახლა:{' '}
                          {formatPercent(stats?.pct)}
                        </p>
                      )}
                    </div>
                  ) : (stats?.pct ?? 100) < (settings?.risk_threshold ?? 75) ||
                    (stats?.streak ?? 0) >= (settings?.streak_risk_min ?? 3) ? (
                    <div className="flex items-start gap-2 rounded-lg bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
                      <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                      <span>რისკშია, სტატუსი ჯერ არ დაუყენებია — გაცდენების დაფაზე ჩანს.</span>
                    </div>
                  ) : (
                    <p className="rounded-lg bg-ok-soft px-3 py-2.5 text-[13px] text-ok">
                      რისკის ნიშნები არ არის.
                    </p>
                  )}
                </Card>
                )}

                {/* --- დასწრების ისტორია (არჩეულ ეტაპზე) --- */}
                <Card
                  title={`დასწრების ისტორია · ${stageLabel(shownStage)}`}
                  className="lg:col-span-3"
                >
                  {stageTimeline.length === 0 && !isCurrentStage ? (
                    <p className="py-6 text-center text-[13px] text-ink-mute">
                      ამ ეტაპის დასწრება ცალკეული გაკვეთილებით არ ინახება — მხოლოდ ჯამური შედეგია.
                    </p>
                  ) : (
                    <AttendanceTimeline timeline={stageTimeline} />
                  )}
                </Card>

                {/* --- ეტაპები --- */}
                <div className="lg:col-span-1">
                  <StageResults studentId={id} enrollment={active} canEdit lecturerMode={!isStaff} />
                </div>

                {/* --- კომენტარები --- */}
                <div className="lg:col-span-2">
                  <StudentNotes studentId={id} groupId={active.group.id} />
                </div>
              </div>
            </>
          )}
        </>
      )}

      <StudentModal open={editOpen} onClose={() => setEditOpen(false)} student={student} />

      {active && (
        <ConfirmDialog
          open={dropOpen}
          onClose={() => setDropOpen(false)}
          onConfirm={() => void drop()}
          busy={dropEnrollment.isPending}
          danger
          confirmLabel="შეწყვეტა"
          title="სწავლის შეწყვეტა"
          message={
            <div className="space-y-3">
              <p>
                <span className="font-medium text-ink">{student.fullName}</span> ჯგუფიდან{' '}
                <span className="tabular font-medium text-ink">{active.group.name}</span> გადის:
                ჩარიცხვა იხურება, ჟურნალის აქტიურ სიაში აღარ გამოჩნდება და დასწრება აღარ
                ეღრიცხება.
              </p>
              <p className="text-[12px] text-ink-mute">
                ეს დაფის „შეწყვიტა" ნიშანი არ არის — ისტორია და ეტაპების შედეგები რჩება, ხოლო
                შეცდომით დაჭერა იმავე ადგილიდან ბრუნდება.
              </p>
              <Textarea
                label="მიზეზი"
                rows={2}
                value={dropReason}
                onChange={(e) => setDropReason(e.target.value)}
                placeholder="მაგ. სამსახურის გამო ვეღარ აგრძელებს"
                hint="კომენტარების ისტორიაში ჩაიწერება"
              />
            </div>
          }
        />
      )}

      {active && (
        <FlagModal
          open={flagOpen}
          onClose={() => setFlagOpen(false)}
          enrollment={active}
          studentName={student.fullName}
        />
      )}
    </>
  );
}

// ============================================================

function Metric({
  label,
  value,
  color,
  danger,
}: {
  label: string;
  value: string;
  color?: string;
  danger?: boolean;
}) {
  return (
    <div className="rounded-[10px] border border-line bg-surface p-4 shadow-xs">
      <p className="text-[12px] font-medium text-ink-mute">{label}</p>
      <p
        className={clsx('tabular mt-1 text-2xl font-semibold', danger && 'text-danger')}
        style={color && !danger ? { color } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-line py-2.5 text-[13px] last:border-0">
      <dt className="text-ink-mute">{label}</dt>
      <dd className="text-right font-medium text-ink">{value}</dd>
    </div>
  );
}

function FlagModal({
  open,
  onClose,
  enrollment,
  studentName,
}: {
  open: boolean;
  onClose: () => void;
  enrollment: CardEnrollment;
  studentName: string;
}) {
  const toast = useToast();
  const setFlag = useSetFlag();
  const removeFlag = useRemoveFlag();
  const [note, setNote] = useState(enrollment.flag?.note ?? '');

  async function apply(flagType: FlagType) {
    try {
      await setFlag.mutateAsync({ enrollmentId: enrollment.id, flagType, note: note || undefined });
      toast.success(`სტატუსი: ${FLAG_LABEL[flagType]}`);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ შეიცვალა');
    }
  }

  async function clear() {
    try {
      await removeFlag.mutateAsync(enrollment.id);
      toast.success('სტატუსი მოიხსნა');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ მოიხსნა');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="რისკის სტატუსი"
      description={`${studentName} · ${enrollment.group.name}`}
    >
      <div className="space-y-4">
        <Textarea
          label="შენიშვნა"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="მაგ. დავურეკე, არ უპასუხა"
        />
        <div className="flex flex-wrap gap-2">
          {FLAG_TYPES.map((t) => (
            <Button
              key={t}
              size="sm"
              variant={enrollment.flag?.flagType === t ? 'primary' : 'secondary'}
              onClick={() => void apply(t)}
              loading={setFlag.isPending}
            >
              {FLAG_ICON[t]} {FLAG_LABEL[t]}
            </Button>
          ))}
          {enrollment.flag && (
            <Button
              size="sm"
              variant="ghost"
              icon={<X className="size-4" />}
              onClick={() => void clear()}
              loading={removeFlag.isPending}
            >
              მოხსნა
            </Button>
          )}
        </div>
        <p className="text-[12px] text-ink-mute">
          „შეწყვიტა" დაფის ნიშანია: სტუდენტი სიებში რჩება და დასწრებაც ეღრიცხება, მხოლოდ
          საშუალოებში აღარ ითვლება. სწავლის რეალურად შესაწყვეტად იხილე „ჩარიცხვა" →
          „სწავლის შეწყვეტა".
        </p>
      </div>
    </Modal>
  );
}
